import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle2, Circle, AlertTriangle, MapPin, CalendarDays, Tag, Mail, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const ACT_TYPES = [["call", "Call"], ["email", "Email"], ["meeting", "Meeting"], ["interview", "Interview"], ["note", "Note"], ["follow_up", "Follow-up"]];

function LogActivity({ personId, onSaved }) {
  const [a, setA] = useState({ type: "call", summary: "", occurred_at: "", follow_up_date: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/referral-partner/referrals/${personId}/activities`, a); toast.success("Activity logged"); setA({ type: a.type, summary: "", occurred_at: "", follow_up_date: "" }); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="rp-log-activity">
      <div className="flex flex-wrap gap-1.5">{ACT_TYPES.map(([k, l]) => (
        <button key={k} type="button" onClick={() => setA({ ...a, type: k })} data-testid={`rp-act-type-${k}`}
          className={`rounded-full px-2.5 py-1 text-xs border ${a.type === k ? "border-[#006090] bg-[#006090]/10 text-[#006090]" : "border-slate-200 text-slate-600"}`}>{l}</button>))}</div>
      <Textarea rows={2} placeholder="What happened? (e.g. call outcome, interview notes, next step)" value={a.summary} onChange={(e) => setA({ ...a, summary: e.target.value })} data-testid="rp-act-summary" />
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-slate-500">When<Input type="datetime-local" value={a.occurred_at} onChange={(e) => setA({ ...a, occurred_at: e.target.value })} data-testid="rp-act-when" /></label>
        <label className="text-xs text-slate-500">Follow-up date<Input type="date" value={a.follow_up_date} onChange={(e) => setA({ ...a, follow_up_date: e.target.value })} data-testid="rp-act-followup" /></label>
      </div>
      <Button size="sm" className="bg-[#006090] hover:bg-[#004E75]" disabled={busy || !a.summary.trim()} onClick={save} data-testid="rp-act-save">Log Activity</Button>
    </div>
  );
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const STATUS_TONE = {
  "Submitted": "bg-slate-100 text-slate-600", "Under Review": "bg-sky-100 text-sky-700",
  "In Process": "bg-indigo-100 text-indigo-700", "Interviewing": "bg-violet-100 text-violet-700",
  "Approved / Progressing": "bg-amber-100 text-amber-700", "Program / Reserve": "bg-teal-100 text-teal-700",
  "Career / Completed": "bg-emerald-100 text-emerald-700", "Closed": "bg-rose-100 text-rose-700",
};

export default function ReferralDetail() {
  const { personId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api.get(`/referral-partner/referrals/${personId}`)
      .then((r) => setData(r.data))
      .catch((e) => { setErr(apiError(e)); if (e?.response?.status !== 403 && e?.response?.status !== 404) toast.error(apiError(e)); });
  }, [personId, tick]);

  if (err) return (
    <div data-testid="referral-detail-error" className="max-w-lg">
      <button onClick={() => navigate("/referrals/list")} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" />My Referrals</button>
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
        <p className="text-slate-700">This referral isn't available to you.</p>
      </div>
    </div>
  );
  if (!data) return <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-slate-100" />)}</div>;

  const reachedKeys = new Set((data.milestones || []).map((m) => m.key));

  return (
    <div data-testid="referral-detail-page" className="max-w-3xl">
      <button onClick={() => navigate("/referrals/list")} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2" data-testid="referral-detail-back"><ArrowLeft className="w-4 h-4" />My Referrals</button>
      <PageHeader title={data.name} description="Your referral's progress." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Summary */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3" data-testid="referral-detail-summary">
          <div>
            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_TONE[data.public_status] || "bg-slate-100 text-slate-600"}`} data-testid="referral-detail-status">{data.public_status}</span>
          </div>
          <p className="text-sm text-slate-600 flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-slate-400" />Submitted {fmtDate(data.submitted_at)}</p>
          <p className="text-sm text-slate-600 flex items-center gap-1.5"><Tag className="w-4 h-4 text-slate-400" />Source: {data.source}</p>
          {(data.city || data.state) && <p className="text-sm text-slate-600 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" />{[data.city, data.state].filter(Boolean).join(", ")}</p>}
          {data.email && <p className="text-sm text-slate-600 flex items-center gap-1.5 break-all"><Mail className="w-4 h-4 text-slate-400" />{data.email}</p>}
          {data.mobile && <p className="text-sm text-slate-600 flex items-center gap-1.5"><Phone className="w-4 h-4 text-slate-400" />{data.mobile}</p>}
          {data.follow_up_date && <p className="text-sm text-amber-700" data-testid="referral-detail-followup">Follow up by {data.follow_up_date}</p>}
          {(data.needs_attention || []).length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5" data-testid="referral-detail-attention">
              {data.needs_attention.map((a, i) => (
                <p key={i} className="text-xs text-amber-700 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{a.action}</p>
              ))}
            </div>
          )}
        </div>

        {/* Milestone timeline */}
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="referral-detail-timeline">
          <h3 className="font-display font-semibold text-slate-900 mb-4">Milestone Progress</h3>
          <ol className="relative border-l-2 border-slate-100 ml-2 space-y-5">
            {(data.milestones || []).map((m) => (
              <li key={m.key} className="ml-4" data-testid={`referral-milestone-${m.key}`}>
                <span className="absolute -left-[9px] flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500"><CheckCircle2 className="w-3 h-3 text-white" /></span>
                <p className="text-sm font-medium text-slate-800">{m.label}</p>
                <p className="text-[11px] text-slate-400">{fmtDate(m.occurred_at)}</p>
              </li>
            ))}
            {["first_interview", "final_interview", "approved", "reserve", "career"].filter((k) => !reachedKeys.has(k)).slice(0, 1).map((k) => (
              <li key="next" className="ml-4 opacity-50" data-testid="referral-milestone-next">
                <span className="absolute -left-[9px] flex items-center justify-center w-4 h-4 rounded-full bg-white border-2 border-slate-300"><Circle className="w-2 h-2 text-slate-300" /></span>
                <p className="text-sm text-slate-500">Upcoming milestones will appear as your referral progresses.</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="font-display font-semibold text-slate-900 mb-3">Log a call, meeting or note</h3>
          <LogActivity personId={personId} onSaved={() => setTick((t) => t + 1)} />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="rp-my-activities">
          <h3 className="font-display font-semibold text-slate-900 mb-3">My activity</h3>
          {(data.my_activities || []).length === 0 ? <p className="text-sm text-slate-500">Nothing logged yet.</p> : (
            <ul className="space-y-2">{data.my_activities.map((a) => (
              <li key={a.id} className="text-sm" data-testid={`rp-my-activity-${a.id}`}><span className="text-xs rounded bg-slate-100 px-1.5 py-0.5 mr-1.5">{a.type_label}</span>{a.summary}<span className="block text-[11px] text-slate-400">{fmtDate(a.occurred_at)}{a.follow_up_date ? ` · follow up ${a.follow_up_date}` : ""}</span></li>))}</ul>)}
        </div>
      </div>
    </div>
  );
}
